import React, { createContext, useContext, useState, useRef, useCallback } from 'react';

const ScrollToTopContext = createContext(null);

/**
 * ScrollToTopProvider - Provides scroll-to-top functionality via React Context
 * 
 * PERFORMANCE OPTIMIZATIONS:
 * - Throttled scroll listener (100ms)
 * - Passive event listener (non-blocking)
 * - Proper cleanup on unmount
 * 
 * Usage:
 * 1. Wrap your app with <ScrollToTopProvider>
 * 2. Attach containerRef to your scrollable container
 * 3. Use useScrollToTop() hook in any child component
 */
export const ScrollToTopProvider = ({ children }) => {
  const [showButton, setShowButton] = useState(false);
  const containerRef = useRef(null);
  const lastScrollTimeRef = useRef(0);
  const THROTTLE_MS = 100;

  const checkScrollPosition = useCallback((threshold = 0.2) => {
    if (!containerRef.current) return;
    
    const scrollTop = containerRef.current.scrollTop;
    const containerHeight = containerRef.current.clientHeight;
    const scrollThreshold = containerHeight * threshold;
    const shouldShow = scrollTop > scrollThreshold;
    
    setShowButton(shouldShow);
  }, [showButton]);

  const handleScroll = useCallback((threshold = 0.2) => {
    const now = Date.now();
    
    // ✅ Throttle: Only execute if enough time has passed
    if (now - lastScrollTimeRef.current < THROTTLE_MS) {
      return;
    }
    
    lastScrollTimeRef.current = now;
    checkScrollPosition(threshold);
  }, [checkScrollPosition]);

  const scrollToTop = useCallback(() => {
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  }, []);

  const value = {
    showButton,
    scrollToTop,
    containerRef,
    checkScrollPosition,
    handleScroll
  };

  return (
    <ScrollToTopContext.Provider value={value}>
      {children}
    </ScrollToTopContext.Provider>
  );
};

/**
 * useScrollToTop - Hook to access scroll-to-top functionality
 * 
 * @returns {Object} { showButton, scrollToTop, containerRef, checkScrollPosition, handleScroll }
 * @throws {Error} If used outside of ScrollToTopProvider
 */
export const useScrollToTop = () => {
  const context = useContext(ScrollToTopContext);
  if (!context) {
    throw new Error('useScrollToTop must be used within ScrollToTopProvider');
  }
  return context;
};

